import { ValidationError } from '@domain/errors';
import type { Address, AddressDraft, AddressImportRow, AddressReview, FlaggedReview } from '@domain/entities';
import type {
  AddressQuery,
  AddressRepository,
  ReviewRepository,
} from '@domain/repositories';
import type { Page } from '@domain/value-objects';
import type { AccessPolicy } from '../../ports';
import type { UseCase } from '../use-case';

export class SearchAddresses implements UseCase<AddressQuery, Page<Address>> {
  constructor(private readonly repo: AddressRepository) {}
  execute(query: AddressQuery): Promise<Page<Address>> {
    return this.repo.search(query);
  }
}

export interface SaveAddressInput {
  id: string | null;
  draft: AddressDraft;
}

export class SaveAddress implements UseCase<SaveAddressInput, Address> {
  constructor(private readonly repo: AddressRepository) {}

  async execute({ id, draft }: SaveAddressInput): Promise<Address> {
    const fields: Record<string, string> = {};
    if (!draft.name.trim()) fields.name = 'Le nom est requis';
    if (!draft.city.trim()) fields.city = 'La ville est requise';
    if (Object.keys(fields).length > 0) {
      throw new ValidationError('Formulaire incomplet', fields);
    }
    const normalized: AddressDraft = { ...draft, name: draft.name.trim() };
    return id === null ? this.repo.create(normalized) : this.repo.update(id, normalized);
  }
}

/** Suppression (unitaire ou en masse) : réservé au super-admin. */
export class DeleteAddresses implements UseCase<readonly string[], void> {
  constructor(
    private readonly repo: AddressRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(ids: readonly string[]): Promise<void> {
    this.policy.assertSuperAdmin('supprimer une adresse');
    if (ids.length === 0) throw new ValidationError('Aucune adresse sélectionnée');
    await this.repo.remove(ids);
  }
}

export interface SetAddressesActiveInput {
  ids: readonly string[];
  active: boolean;
}

export class SetAddressesActive implements UseCase<SetAddressesActiveInput, Address[]> {
  constructor(private readonly repo: AddressRepository) {}
  execute({ ids, active }: SetAddressesActiveInput): Promise<Address[]> {
    return this.repo.setActive(ids, active);
  }
}

export class GetAddressReviews implements UseCase<string, AddressReview[]> {
  constructor(private readonly repo: AddressRepository) {}
  execute(addressId: string): Promise<AddressReview[]> {
    return this.repo.listReviews(addressId);
  }
}

export class DeleteAddressReview implements UseCase<string, void> {
  constructor(
    private readonly repo: AddressRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(reviewId: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer un avis');
    await this.repo.deleteReview(reviewId);
  }
}

/** Étape 1 : lecture du fichier, aucune écriture. */
export class PreviewAddressImport implements UseCase<File, AddressImportRow[]> {
  constructor(private readonly repo: AddressRepository) {}
  execute(file: File): Promise<AddressImportRow[]> {
    return this.repo.parseImportFile(file);
  }
}

/** Étape 2 : insertion effective après confirmation explicite. */
export class ConfirmAddressImport implements UseCase<readonly AddressImportRow[], Address[]> {
  constructor(private readonly repo: AddressRepository) {}

  async execute(rows: readonly AddressImportRow[]): Promise<Address[]> {
    if (rows.length === 0) throw new ValidationError('Aucune ligne à importer');
    return this.repo.importRows(rows);
  }
}

export class ListFlaggedReviews implements UseCase<void, FlaggedReview[]> {
  constructor(private readonly repo: ReviewRepository) {}
  execute(): Promise<FlaggedReview[]> {
    return this.repo.listFlagged();
  }
}

export class DismissReview implements UseCase<string, void> {
  constructor(private readonly repo: ReviewRepository) {}
  execute(id: string): Promise<void> {
    return this.repo.dismiss(id);
  }
}

export class DeleteReview implements UseCase<string, void> {
  constructor(
    private readonly repo: ReviewRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(id: string): Promise<void> {
    this.policy.assertSuperAdmin('supprimer un avis');
    await this.repo.remove(id);
  }
}
